import { LANDMARKS } from './helpers'

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const rotatePointY = (point, cosA, sinA) => {
  const x = Number(point?.x) || 0
  const y = Number(point?.y) || 0
  const z = Number(point?.z) || 0
  return {
    x: x * cosA - z * sinA,
    y,
    z: x * sinA + z * cosA
  }
}

const computeFrontalScore = (points, angle) => {
  if (!points?.length) return Infinity
  const lEye = points[LANDMARKS.leftEye]
  const rEye = points[LANDMARKS.rightEye]
  const lInner = points[LANDMARKS.leftEyeInner]
  const rInner = points[LANDMARKS.rightEyeInner]
  const nose = points[LANDMARKS.noseTip]
  const mLeft = points[LANDMARKS.mouthLeft]
  const mRight = points[LANDMARKS.mouthRight]
  if (!lEye || !rEye || !lInner || !rInner || !nose) return Infinity

  const cosA = Math.cos(angle)
  const sinA = Math.sin(angle)
  const leftEye = rotatePointY(lEye, cosA, sinA)
  const rightEye = rotatePointY(rEye, cosA, sinA)
  const leftInner = rotatePointY(lInner, cosA, sinA)
  const rightInner = rotatePointY(rInner, cosA, sinA)
  const noseTip = rotatePointY(nose, cosA, sinA)
  const mouthLeft = mLeft ? rotatePointY(mLeft, cosA, sinA) : null
  const mouthRight = mRight ? rotatePointY(mRight, cosA, sinA) : null

  const eyeSpan = Math.max(1e-4, Math.abs(rightEye.x - leftEye.x))
  const eyeMidX = (leftEye.x + rightEye.x) * 0.5
  const noseOffset = Math.abs(noseTip.x - eyeMidX) / eyeSpan
  const eyeDepthDelta = Math.abs((leftEye.z || 0) - (rightEye.z || 0)) / eyeSpan

  const innerSpan = Math.max(1e-4, Math.abs(rightInner.x - leftInner.x))
  const innerMidX = (leftInner.x + rightInner.x) * 0.5
  const innerDepthDelta = Math.abs((leftInner.z || 0) - (rightInner.z || 0)) / innerSpan
  const innerCenterOffset = Math.abs(innerMidX - eyeMidX) / eyeSpan

  let mouthCenterOffset = 0
  if (mouthLeft && mouthRight) {
    const mouthMidX = (mouthLeft.x + mouthRight.x) * 0.5
    mouthCenterOffset = Math.abs(mouthMidX - eyeMidX) / eyeSpan
  }

  return (
    noseOffset * 1.25 +
    eyeDepthDelta * 1.05 +
    innerDepthDelta * 0.75 +
    innerCenterOffset * 0.45 +
    mouthCenterOffset * 0.35
  )
}

export const estimateCanonicalYawCentering = (
  points,
  { maxRadians = 0.52, stepRadians = 0.012, minConfidence = 0.1 } = {}
) => {
  if (!points?.length) {
    return {
      angle: 0,
      confidence: 0,
      baseScore: Infinity,
      bestScore: Infinity
    }
  }
  const maxR = clamp(Number(maxRadians) || 0.52, 0.08, 0.9)
  const step = clamp(Number(stepRadians) || 0.012, 0.004, 0.06)
  const baseScore = computeFrontalScore(points, 0)
  if (!Number.isFinite(baseScore)) {
    return {
      angle: 0,
      confidence: 0,
      baseScore,
      bestScore: baseScore
    }
  }

  let bestAngle = 0
  let bestScore = baseScore
  for (let a = -maxR; a <= maxR + 1e-6; a += step) {
    const score = computeFrontalScore(points, a)
    if (score < bestScore) {
      bestScore = score
      bestAngle = a
    }
  }

  const improvement = Math.max(0, baseScore - bestScore)
  const confidence = baseScore > 1e-5 ? clamp(improvement / baseScore, 0, 1) : 0
  if (Math.abs(bestAngle) < 0.01 || confidence < minConfidence) {
    return {
      angle: 0,
      confidence,
      baseScore,
      bestScore
    }
  }
  return {
    angle: clamp(bestAngle, -maxR, maxR),
    confidence,
    baseScore,
    bestScore
  }
}

export const centerCanonicalYaw = (
  points,
  {
    enabled = true,
    strength = 1,
    maxRadians = 0.52,
    minConfidence = 0.1
  } = {}
) => {
  if (!enabled || !points?.length) {
    return { appliedYaw: 0, estimatedYaw: 0, confidence: 0, skipped: true }
  }

  const estimate = estimateCanonicalYawCentering(points, { maxRadians, minConfidence })
  if (!estimate?.angle) {
    return {
      appliedYaw: 0,
      estimatedYaw: estimate?.angle || 0,
      confidence: estimate?.confidence || 0,
      skipped: true
    }
  }

  const strengthClamped = clamp(Number(strength) || 1, 0, 1.5)
  const appliedYaw = clamp(estimate.angle * strengthClamped, -maxRadians, maxRadians)
  if (Math.abs(appliedYaw) < 0.005) {
    return {
      appliedYaw: 0,
      estimatedYaw: estimate.angle,
      confidence: estimate.confidence,
      skipped: true
    }
  }

  const cosA = Math.cos(appliedYaw)
  const sinA = Math.sin(appliedYaw)
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    if (!p) continue
    const x = Number(p.x) || 0
    const z = Number(p.z) || 0
    p.x = x * cosA - z * sinA
    p.z = x * sinA + z * cosA
  }

  const lEye = points[LANDMARKS.leftEye]
  const rEye = points[LANDMARKS.rightEye]
  if (lEye && rEye) {
    const shiftX = ((lEye.x || 0) + (rEye.x || 0)) * 0.5
    const shiftZ = ((lEye.z || 0) + (rEye.z || 0)) * 0.5
    for (let i = 0; i < points.length; i += 1) {
      const p = points[i]
      if (!p) continue
      p.x -= shiftX
      p.z -= shiftZ
    }
  }

  return {
    appliedYaw,
    estimatedYaw: estimate.angle,
    confidence: estimate.confidence,
    skipped: false
  }
}

export default {
  centerCanonicalYaw,
  estimateCanonicalYawCentering
}
